import { Component, type ReactNode } from "react";

const RELOAD_KEY = "vp-chunk-reload-at";
const RELOAD_COOLDOWN_MS = 15_000;

/**
 * A failed dynamic import must never leave a blank page. The usual cause is a
 * tab held open across a deploy: the hashed chunk it asks for no longer exists.
 * That case self-heals with one reload, so we do exactly one, then fall back to
 * a visible message rather than reloading in a loop.
 */
function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|ChunkLoadError|Loading chunk/i.test(message);
}

interface State { error: Error | null }

export class RouteBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    if (!isChunkLoadError(error)) return;
    // Guard on elapsed time rather than a "have we reloaded" flag. This boundary
    // mounts successfully before its lazy child fails, so it has no reliable
    // success signal to clear such a flag with, and clearing it on mount would
    // re-arm the reload on every pass and loop forever.
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // Private browsing can throw on storage access; show the message instead.
      return;
    }
    window.location.reload();
  }

  private retry = (): void => {
    try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* ignore */ }
    window.location.reload();
  };

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    return (
      <div className="vp-boundary" role="alert">
        <span className="vp-kicker" data-tone="signal">Signal lost</span>
        <h1>{chunk ? "This page needs a refresh." : "Something went wrong."}</h1>
        <p>
          {chunk
            ? "VentureProof updated while this tab was open, so part of the app could not load. Reloading picks up the current version."
            : "The surface failed to render. Reloading usually clears it. If it keeps happening, the details below help us fix it."}
        </p>
        <button type="button" className="vp-btn" data-variant="primary" onClick={this.retry}>Reload VentureProof</button>
        <code>{error.message}</code>
      </div>
    );
  }
}
